import React, { useCallback, useMemo } from 'react';
import { Collapse, Descriptions, Tag, Button, Typography, Row, Col, message, Space } from 'antd';
import { UserOutlined, FileTextOutlined, TableOutlined, DownloadOutlined } from '@ant-design/icons';
import { ISapSanitizedProduct } from '../../../types';
import ProductPreviewCard from './ProductPreviewCard';
import ApprovalInfo from '../../../atoms/approval-info';
import { exportStyledExcel, IExcelExportColumn } from '../../shared/utils/exportStyledExcel';

const { Panel } = Collapse;
const { Text, Link } = Typography;

interface Props {
    data: ISapSanitizedProduct;
}

const DetailSection: React.FC<{ title: React.ReactNode; icon: React.ReactNode; children: React.ReactNode; extra?: React.ReactNode }> = ({ title, icon, children, extra }) => {
    return (
        <Collapse defaultActiveKey={['1']} ghost style={{ marginBottom: 24, background: '#fff', borderRadius: 8 }}>
            <Panel header={<span>{icon} <span style={{ marginLeft: 8 }}>{title}</span></span>} key="1" extra={extra}>
                {children}
            </Panel>
        </Collapse>
    );
};

const SapMasterProductDetailView: React.FC<Props> = ({ data }) => {
    const exportColumns = useMemo(
        () =>
            (data.columns || [])
                .filter((column): column is NonNullable<typeof column> & { key: string; label: string } => Boolean(column?.key && column?.label))
                .map<IExcelExportColumn<Record<string, unknown>>>(column => ({
                    key: column.key,
                    label: column.label,
                })),
        [data.columns],
    );
    const exportRows = useMemo(() => data.payload || [], [data.payload]);

    const getStatusColor = useCallback((status: string) => {
        switch (status) {
            case "Approved":
                return "green"
            case "Reject":
            case 'Cancel':
                return "red"
            case 'Waiting Approval':
                return "blue"
            default:
                return "blue"
        }
    }, [])

    const handleExport = useCallback(async () => {
        try {
            await exportStyledExcel({
                filename: `SAP_Product_${data.record_id || 'detail'}.xlsx`,
                sheetName: 'Product Data',
                title: `SAP Product - ${data.record_id || '-'}`,
                columns: exportColumns,
                rows: exportRows as Record<string, unknown>[],
            })
        } catch (_error) {
            void message.error('Failed to export product Excel')
        }
    }, [data.record_id, exportColumns, exportRows])

    const handleCopyLink = useCallback(async () => {
        try {
            await navigator.clipboard.writeText(window.location.href)
            void message.success("Product link copied")
        } catch (_error) {
            void message.error("Failed to copy product link")
        }
    }, [])

    return (
        <>
            <Row gutter={24}>
                <Col xs={24}>
                    <DetailSection title="User Information" icon={<UserOutlined />}>
                        <Descriptions column={{ xs: 1, sm: 2 }}>
                            <Descriptions.Item label="Employee ID">{data.employee_id}</Descriptions.Item>
                            <Descriptions.Item label="Fullname">{data.fullname}</Descriptions.Item>
                            <Descriptions.Item label="Level">{data.level || '-'}</Descriptions.Item>
                            <Descriptions.Item label="Department">{data.department || '-'}</Descriptions.Item>
                        </Descriptions>
                    </DetailSection>
                </Col>
            </Row>

            <Row gutter={24}>
                <Col xs={24}>
                    <DetailSection title="Request Details" icon={<FileTextOutlined />}>
                        <Descriptions column={{ xs: 1, sm: 2 }}>
                            <Descriptions.Item label="Record ID">
                                {data.record_id ? (
                                    <Space size={8}>
                                        <Text>{data.record_id}</Text>
                                        <Link onClick={() => void handleCopyLink()}>Click link to copy</Link>
                                    </Space>
                                ) : (
                                    "-"
                                )}
                            </Descriptions.Item>
                            <Descriptions.Item label="Type of Request">{data.type_of_request}</Descriptions.Item>
                            <Descriptions.Item label="Approval Status">
                                <Tag color={getStatusColor(data.approval_status)}>
                                    {data.approval_status || '-'}
                                </Tag>
                            </Descriptions.Item>
                            <Descriptions.Item label="Created At">
                                {data.created_at ? new Date(data.created_at).toLocaleString() : '-'}
                            </Descriptions.Item>
                        </Descriptions>
                    </DetailSection>
                </Col>
            </Row>
            <Row gutter={24}>
                <Col xs={24}>
                    <DetailSection
                        title="Product Data"
                        icon={<TableOutlined />}
                        extra={exportRows.length ? (
                            <div onClick={event => event.stopPropagation()}>
                                <Button icon={<DownloadOutlined />} onClick={() => void handleExport()}>
                                    Export Data Updated
                                </Button>
                            </div>
                        ) : undefined}
                    >
                        <ProductPreviewCard columns={data.columns || []} items={data.payload || []} />
                    </DetailSection>
                </Col>
            </Row>

            {data.approval_process_id && (
                <Row gutter={24}>
                    <Col xs={24}>
                        <DetailSection title="Approval Info & Comments" icon={<FileTextOutlined />}>
                            <ApprovalInfo documentData={data} usePanel={false} />
                        </DetailSection>
                    </Col>
                </Row>
            )}
        </>
    );
};

export default SapMasterProductDetailView;
